import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Dumbbell, Salad, Loader2 } from "lucide-react";

export default function CoachAIStudio() {
  const [tab, setTab] = useState("workout");
  return (
    <div className="space-y-10" data-testid="coach-ai-studio">
      <header>
        <div className="overline">AI Coach</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
          Co-pilot for <span className="font-script normal-case" style={{ fontSize: "1.05em", color: "var(--accent)", letterSpacing: 0 }}>coaching.</span>
        </h1>
        <div className="bronze-rule mt-6" />
        <p className="font-soft text-lg mt-6 max-w-xl" style={{ color: "var(--text-secondary)" }}>Generate full programs and meal plans in seconds. Edit, refine, and assign.</p>
      </header>

      <div className="flex gap-3 border-b border-luxe pb-1">
        {[
          { k: "workout", label: "Workout", icon: Dumbbell },
          { k: "nutrition", label: "Nutrition", icon: Salad },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ai-tab-${k}`}
            className={`px-4 py-2 text-sm uppercase tracking-widest border-b-2 ${
              tab === k ? "border-accent text-accent" : "border-transparent text-secondary-luxe"
            }`} style={{ borderBottomColor: tab === k ? "var(--accent)" : "transparent" }}>
            <Icon size={14} className="inline mr-2" /> {label}
          </button>
        ))}
      </div>

      {tab === "workout" ? <WorkoutGen /> : <NutritionGen />}
    </div>
  );
}

function WorkoutGen() {
  const [form, setForm] = useState({ goal: "Hypertrophy + functional strength", experience: "intermediate", equipment: "full gym", days_per_week: 4, duration_minutes: 60, focus: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/workout", form);
      let parsed = null;
      try { parsed = JSON.parse(data.plan_text); } catch { /* leave raw */ }
      setResult({ parsed, raw: data.plan_text });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <form onSubmit={run} className="card-luxe space-y-5" data-testid="ai-workout-form">
        <div><label className="label-luxe">Goal</label><input className="input-luxe" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="ai-workout-goal" /></div>
        <div className="grid grid-cols-2 gap-5">
          <div><label className="label-luxe">Experience</label>
            <select className="input-luxe" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })}>
              <option>beginner</option><option>intermediate</option><option>advanced</option>
            </select>
          </div>
          <div><label className="label-luxe">Equipment</label><input className="input-luxe" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} /></div>
          <div><label className="label-luxe">Days/week</label><input type="number" min="1" max="7" className="input-luxe" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: parseInt(e.target.value || "4", 10) })} /></div>
          <div><label className="label-luxe">Duration (min)</label><input type="number" className="input-luxe" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || "60", 10) })} /></div>
        </div>
        <div><label className="label-luxe">Focus (optional)</label><input className="input-luxe" value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="posterior chain, mobility…" /></div>
        <button disabled={busy} className="btn-primary" data-testid="ai-workout-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? "Composing…" : "Generate"}
        </button>
      </form>
      <div className="card-luxe min-h-[300px]" data-testid="ai-workout-result">
        {!result ? <div className="text-secondary-luxe text-sm">Your plan will appear here.</div> : (
          result.parsed ? <PlanRender plan={result.parsed} kind="workout" /> : <pre className="text-xs whitespace-pre-wrap text-secondary-luxe">{result.raw}</pre>
        )}
      </div>
    </div>
  );
}

function NutritionGen() {
  const [form, setForm] = useState({ goal: "Body recomposition with energy", dietary_preference: "omnivore", allergies: "", calorie_target: 2200, protocol: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault(); setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/nutrition", { ...form, calorie_target: parseInt(form.calorie_target || 0, 10) || null });
      let parsed = null;
      try { parsed = JSON.parse(data.plan_text); } catch { /* leave raw */ }
      setResult({ parsed, raw: data.plan_text });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <form onSubmit={run} className="card-luxe space-y-5" data-testid="ai-nutrition-form">
        <div><label className="label-luxe">Goal</label><input className="input-luxe" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-5">
          <div><label className="label-luxe">Dietary preference</label>
            <select className="input-luxe" value={form.dietary_preference} onChange={(e) => setForm({ ...form, dietary_preference: e.target.value })}>
              <option>omnivore</option><option>vegetarian</option><option>vegan</option><option>pescatarian</option><option>carnivore</option><option>keto</option><option>paleo</option>
            </select>
          </div>
          <div><label className="label-luxe">Calories</label><input type="number" className="input-luxe" value={form.calorie_target} onChange={(e) => setForm({ ...form, calorie_target: e.target.value })} /></div>
          <div className="col-span-2"><label className="label-luxe">Allergies</label><input className="input-luxe" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="gluten, dairy…" /></div>
          <div className="col-span-2"><label className="label-luxe">Functional protocol</label>
            <select className="input-luxe" value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}>
              <option value="">None</option>
              <option>Gentle detox</option>
              <option>Gut healing</option>
              <option>Adrenal support</option>
              <option>Liver reset</option>
              <option>Hormone balance</option>
              <option>Cortisol regulation</option>
              <option>Mineral replenishment</option>
              <option>Cycle syncing</option>
              <option>Lymphatic flow</option>
              <option>Blood sugar balance</option>
              <option>Postpartum restoration</option>
              <option>Perimenopause support</option>
            </select>
          </div>
        </div>
        <button disabled={busy} className="btn-primary" data-testid="ai-nutrition-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? "Composing…" : "Generate"}
        </button>
      </form>
      <div className="card-luxe min-h-[300px]" data-testid="ai-nutrition-result">
        {!result ? <div className="text-secondary-luxe text-sm">Your meal plan will appear here.</div> : (
          result.parsed ? <PlanRender plan={result.parsed} kind="nutrition" /> : <pre className="text-xs whitespace-pre-wrap text-secondary-luxe">{result.raw}</pre>
        )}
      </div>
    </div>
  );
}

function PlanRender({ plan, kind }) {
  return (
    <div className="space-y-4 max-h-[600px] overflow-y-auto">
      <div className="overline">{kind === "workout" ? "Plan" : "Daily plan"}</div>
      <h3 className="font-serif text-2xl">{plan.title}</h3>
      <p className="text-secondary-luxe text-sm">{plan.summary}</p>
      {kind === "workout" && plan.weeks?.map((w, wi) => (
        <div key={wi}>
          <div className="overline mt-3">Week {w.week}</div>
          {w.days?.map((d, di) => (
            <div key={di} className="mt-3 border-l border-luxe pl-4">
              <div className="font-medium">{d.day} — <span className="text-accent">{d.focus}</span></div>
              <ul className="mt-2 space-y-1 text-sm text-secondary-luxe">
                {d.exercises?.map((e, ei) => (
                  <li key={ei}>{e.name} · {e.sets}×{e.reps} · rest {e.rest_seconds}s {e.notes && <em className="text-tertiary-luxe">— {e.notes}</em>}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
      {kind === "nutrition" && plan.meals && (
        <>
          {plan.daily_macros && (
            <div className="card-luxe text-sm flex gap-4 flex-wrap">
              <div><div className="overline">Cal</div><div className="font-mono-luxe">{plan.daily_macros.calories}</div></div>
              <div><div className="overline">P</div><div className="font-mono-luxe">{plan.daily_macros.protein}g</div></div>
              <div><div className="overline">C</div><div className="font-mono-luxe">{plan.daily_macros.carbs}g</div></div>
              <div><div className="overline">F</div><div className="font-mono-luxe">{plan.daily_macros.fats}g</div></div>
            </div>
          )}
          {plan.meals.map((m, mi) => (
            <div key={`${m.meal || "meal"}-${mi}`} className="border-l border-luxe pl-4">
              <div className="overline">{m.meal}</div>
              <div className="font-medium">{m.name}</div>
              <div className="text-xs text-tertiary-luxe mt-1">{(m.ingredients || []).join(" · ")}</div>
              {m.macros && <div className="text-xs text-secondary-luxe mt-1">{m.macros.calories} cal · {m.macros.protein}P · {m.macros.carbs}C · {m.macros.fats}F</div>}
              {m.notes && <div className="text-xs italic text-tertiary-luxe mt-1">{m.notes}</div>}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
